import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';

export default function LogCallSheet({ open, onOpenChange, contactId, contactName, dealOptions, onSuccess }) {
  const [direction, setDirection] = useState('outbound');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedDeals, setSelectedDeals] = useState(() => (dealOptions || []).map(d => d.deal_id));
  const [createFollowups, setCreateFollowups] = useState(true);
  const [followUpDays, setFollowUpDays] = useState(3);
  const [submitting, setSubmitting] = useState(false);

  function toggleDeal(dealId) {
    setSelectedDeals(prev =>
      prev.includes(dealId)
        ? prev.filter(d => d !== dealId)
        : [...prev, dealId]
    );
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await api.post(`/api/tracon/command-center/pattern/conversation/${encodeURIComponent(contactId)}/log-call`, {
        duration_seconds: duration ? parseInt(duration, 10) * 60 : null,
        direction,
        notes: notes.trim() || null,
        deal_ids: selectedDeals,
        create_followups: createFollowups,
        follow_up_due_days: followUpDays,
      });
      toast.success('Call logged');
      onSuccess?.();
    } catch (err) {
      toast.error(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Log call with {contactName || 'contact'}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-text-muted">Direction</label>
            <div className="mt-1 flex gap-2">
              {['outbound', 'inbound'].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    direction === d ? 'bg-accent text-white' : 'bg-border/40 text-text-muted hover:text-text'
                  }`}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Duration (minutes, optional)</label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g. 8"
              min="1"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            />
          </div>

          {dealOptions && dealOptions.length > 0 && (
            <div>
              <label className="text-xs font-medium text-text-muted">Linked deals</label>
              <div className="mt-1 space-y-1">
                {dealOptions.map(d => (
                  <label key={d.deal_id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-border/20 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDeals.includes(d.deal_id)}
                      onChange={() => toggleDeal(d.deal_id)}
                      className="accent-accent"
                    />
                    <span className="text-text">{d.deal_account_name || d.deal_app_number || d.deal_id}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-text-muted">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Call notes…"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
              <input
                type="checkbox"
                checked={createFollowups}
                onChange={(e) => setCreateFollowups(e.target.checked)}
                className="accent-accent"
              />
              Create follow-up tasks
            </label>
            {createFollowups && (
              <div className="flex items-center gap-1 text-xs text-text-muted">
                in
                <input
                  type="number"
                  value={followUpDays}
                  onChange={(e) => setFollowUpDays(parseInt(e.target.value, 10) || 3)}
                  min="1"
                  max="30"
                  className="w-12 rounded border border-border bg-bg px-1 py-0.5 text-center text-xs"
                />
                days
              </div>
            )}
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full">
            {submitting ? 'Logging…' : 'Log call'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
